import {
  type BaseDoctorsListParams,
  type City,
  type CityArea,
  type ClinicBranch,
  type Doctor,
  type DoctorService,
  type DoctorsListOrder,
  type PaginatedResponse,
  type ServiceDetails,
  type Slot,
  useV1ClinicBranchesListParallel,
  useV1DoctorsListParallel,
  useV1DoctorsServicesListParallel,
  useV1PremiumDoctorsList,
  useV1SlotsListParallel,
} from "@hermes/api";
import { Temporal, useLocale, useNow } from "@hermes/intl";
import type { GeoPosition } from "@hermes/shared";
import { useScreenDetector } from "@hermes/ui";
import { keepPreviousData, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { useAppConfig } from "#internal/app/config/context";
import { type DoctorCardService } from "#internal/features/doctor-card";

import { getSlotsDateRanges } from "./date-range";
import { mergeDoctorsPages } from "./merging";

const toPageQuery = <T>(
  query?: UseQueryResult<PaginatedResponse<T[]>, Error>,
): PageQuery<T> | undefined =>
  query
    ? {
        results: query.data?.results || [],
        isFetched: query.isFetched,
        isFetching: query.isFetching,
      }
    : undefined;

type PageQuery<T> = {
  isFetched?: boolean;
  isFetching?: boolean;
  results: T[];
};

export type Page = {
  seen: boolean;
  size?: number;
  offset?: number;
  doctors?: PageQuery<Doctor & { premium?: boolean }>;
  branches?: PageQuery<ClinicBranch>;
  services?: PageQuery<DoctorService>;
  slots?: PageQuery<Slot>;
  isFetched?: boolean;
  isFetching?: boolean;
};

export const useDoctorsPages = ({
  sort,
  targetServices,
  city,
  cityArea,
  clinicId,
  position,
  slotDate,
  enablePromo,
  language,
  insurance,
  currentService,
  excludedIds,
  nearestCityArea,
}: {
  sort?: DoctorsListOrder;
  targetServices?: DoctorCardService[];
  city: City;
  cityArea?: CityArea;
  clinicId?: number;
  position?: GeoPosition;
  slotDate?: Temporal.PlainDate;
  enablePromo?: boolean;
  language?: string;
  insurance?: number;
  currentService?: ServiceDetails;
  excludedIds?: number[];
  nearestCityArea?: number;
}) => {
  const { isRobot } = useAppConfig();
  const { isMobile } = useScreenDetector();
  const [locale] = useLocale();

  const now = useNow("day");
  const serviceIds = targetServices?.map((service) => service.id);
  const limit = isRobot ? 100 : isMobile ? 5 : 8;

  const [seen, setSeen] = useState(new Set<number>());

  const params = useMemo(
    () =>
      ({
        ordering: sort,
        limit,
        service: serviceIds,
        city: city.id,
        city_area: cityArea?.id,
        clinic: clinicId,
        lat: position?.lat,
        lng: position?.lng,
        slot_date: slotDate?.toString(),
        language,
        insurance,
        expand: ["clinic_branches"],
        exclude: excludedIds,
        nearest_city_area: nearestCityArea,
      }) satisfies BaseDoctorsListParams,
    [
      sort,
      limit,
      serviceIds?.join(","),
      city.id,
      cityArea?.id,
      clinicId,
      position?.lat,
      position?.lng,
      slotDate?.toString(),
      language,
      insurance,
      excludedIds?.join(","),
      nearestCityArea,
    ],
  );

  useEffect(() => {
    setSeen(new Set());
  }, [params]);

  const isPromoEnabled = Boolean(
    enablePromo &&
      (sort === "default" || sort === undefined) &&
      !position &&
      !slotDate &&
      !clinicId &&
      !cityArea &&
      !language &&
      !insurance &&
      !nearestCityArea,
  );

  const premiumQuery = useV1PremiumDoctorsList(
    {
      city: city.id,
      expand: ["clinic_branches"],
      service: serviceIds,
      exclude: excludedIds,
    },
    { enabled: isPromoEnabled },
  );

  const firstDoctorsQueries = useV1DoctorsListParallel({
    pages: [{ offset: 0, seen: true }],
    ...params,
  });

  const firstDoctorsQuery = firstDoctorsQueries[0];

  const firstPage: Page = useMemo(() => {
    return {
      seen: true,
      offset: 0,
      doctors: {
        isFetched: isPromoEnabled
          ? premiumQuery.isFetched && firstDoctorsQuery?.isFetched
          : firstDoctorsQuery?.isFetched,
        isFetching: isPromoEnabled
          ? premiumQuery.isFetching || firstDoctorsQuery?.isFetching
          : firstDoctorsQuery?.isFetching,
        results: [
          ...((isPromoEnabled &&
            premiumQuery.data?.results.map((doctor) => ({
              ...doctor,
              premium: true,
            }))) ||
            []),
          ...(firstDoctorsQuery?.data?.results ?? []),
        ],
      },
      size:
        (premiumQuery.data?.results.length ?? 3) +
        (firstDoctorsQuery?.data?.results.length ?? limit),
    };
  }, [isPromoEnabled, premiumQuery.state, firstDoctorsQueries.state, limit]);

  let pages: Page[] = [firstPage];
  const RESERVED_PAGES = 1;
  const firstPageReady = Boolean(firstPage.doctors?.isFetched);
  const count = firstDoctorsQuery?.data?.count;

  const additionalPages = useMemo(() => {
    if (!count) return [];
    return Array.from({ length: Math.ceil(count / limit) - 1 }, (_, index) => {
      const offset = (index + 1) * limit;
      const remaining = count - offset;
      return {
        offset,
        seen: isRobot || seen.has(index + RESERVED_PAGES),
        size: remaining >= limit ? limit : remaining,
      };
    });
  }, [count, limit, isRobot, seen]);

  pages = pages.concat(additionalPages);

  const doctorsQueries = useV1DoctorsListParallel({
    pages: pages.slice(RESERVED_PAGES),
    ...params,
  });

  for (let i = RESERVED_PAGES; i < pages.length; i++) {
    pages[i]!.doctors = toPageQuery(doctorsQueries[i - RESERVED_PAGES]);
  }

  const seenBranches = new Set<number>();
  const clinicBranchesIds = pages.map((page) => {
    if (!page.doctors?.results) return [];
    return page.doctors.results
      .flatMap((doctor) => doctor.clinic_branches || [])
      .filter((branchId) => {
        if (seenBranches.has(branchId)) return false;
        seenBranches.add(branchId);
        return true;
      });
  });

  const branchesQueries = useV1ClinicBranchesListParallel(
    {
      groupedIds: clinicBranchesIds,
      expand: ["clinic_slug", "closest_stations", "city", "city_area"],
      lat: position?.lat,
      lng: position?.lng,
      localeBaseName: locale.baseName,
      clinic: clinicId,
    },
    {
      placeholderData: keepPreviousData,
      enabled: firstPageReady,
    },
  );

  const servicesQueries = useV1DoctorsServicesListParallel(
    {
      groupedDoctorIds: pages.map(
        (page) => page.doctors?.results.map((doctor) => doctor.id) || [],
      ),
      service: currentService?.type === "procedure" ? [] : serviceIds,
      clinic_branch: clinicId ? clinicBranchesIds[0] : undefined,
      expand: ["service_name", "service_type"],
    },
    {
      placeholderData: keepPreviousData,
      enabled: firstPageReady,
    },
  );

  const dateRanges = getSlotsDateRanges({
    queries: servicesQueries,
    today: slotDate ?? now.toPlainDate(),
    dateRangeLength: 4,
    maxDaysPerRange: 7,
  });

  const slotsQueries = useV1SlotsListParallel(
    { dateRanges },
    {
      placeholderData: keepPreviousData,
      enabled: firstPageReady,
    },
  );

  const assignToPages = <T>(
    key: Extract<keyof Page, "doctors" | "branches" | "services" | "slots">,
    queries: UseQueryResult<PaginatedResponse<T[]>, Error>[],
  ) => {
    for (let i = 0; i < pages.length; i++) {
      pages[i]![key] = toPageQuery(queries[i]) as any; // <= TSC happy
    }
  };

  assignToPages("branches", branchesQueries);
  assignToPages("services", servicesQueries);
  assignToPages("slots", slotsQueries);

  const mergedPages = mergeDoctorsPages({
    targetServices,
    dateRanges,
    pages,
    currentService,
    clinicId,
  });

  return {
    count,
    limit,
    rawPages: pages,
    pages: mergedPages,
    premiumCount: premiumQuery.data?.count ?? 0,
    fetchPage(page: number) {
      if (!seen.has(page)) {
        setSeen((prev) => new Set([...prev, page]));
      }
    },
  };
};
